import React from "react";
import { API_ROOT } from "../config";
import { Link } from "react-router-dom";
import { NotebookTabs } from "lucide-react";
import TrainingPageHeader from "../components/TrainingPageHeader";
import TrainingTextEditor from "../components/TrainingTextEditor";
import TrainingVideoEditor from "../components/TrainingVideoEditor";
import TrainingSliderEditor from "../components/TrainingSliderEditor";
import TrainingAccordionEditor from "../components/TrainingAccordionEditor";
import TrainingQuizEditor from "../components/TrainingQuizEditor";
import "../styles/TrainingAdminView.css";
import { getResponseError, getNetworkError } from "../utils/fetchError";

class TrainingEditForm extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      formation: null,
      loading: true,
      error: null,
      selectedBlockId: null,
      editingBlocks: {},
      newSectionForms: {},
      newBlockForm: { name: "", mode: "", pass: "" },
      saving: false,
      saveError: null,
      saveSuccess: false,
      portraitError: "",
      portraitFileName: null,
    };
  }

  async componentDidMount() {
    const { slug } = this.props.match.params;
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_ROOT}formations/${slug}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(await getResponseError(res));
      const data = await res.json();
      this.setState({
        formation: data,
        loading: false,
        selectedBlockId: data.blocks[0]?._id || null,
      });
    } catch (err) {
      this.setState({ error: getNetworkError(err), loading: false });
    }
  }

  handleUpdate = async () => {
    const { formation } = this.state;
    this.setState({ saving: true, saveError: null, saveSuccess: false });

    const payload = {
      name: formation.name,
      description: formation.description,
      portrait: formation.portrait || null,
      mandatory: formation.mandatory,
      order: formation.alwaysAccessible ? null : formation.order,
      blocks: formation.blocks.map((block, bi) => ({
        _id: block._id,
        name: block.name,
        mode: block.mode,
        pass: block.pass,
        order: bi + 1,
        sections: block.sections.map((sec, si) => ({
          _id: sec._id,
          name: sec.name,
          type: sec.type,
          order: si + 1,
          ...(sec.type === "text" && { textContent: sec.textContent }),
          ...(sec.type === "video" && { videoContent: sec.videoContent }),
          ...(sec.type === "slider" && { sliderContent: sec.sliderContent }),
          ...(sec.type === "accordion" && {
            accordionContent: sec.accordionContent,
          }),
          ...(sec.type === "quiz" && { quizContent: sec.quizContent }),
        })),
      })),
    };

    try {
      const token = localStorage.getItem("token");
      const body = JSON.stringify(payload);
      const bodySizeKB = new Blob([body]).size / 1024;
      if (bodySizeKB > 4500) {
        this.setState({
          saving: false,
          saveError: `El payload es demasiado grande (${bodySizeKB.toFixed(0)} KB). Optimiza la imagen de portada antes de guardar.`,
        });
        return;
      }
      const res = await fetch(`${API_ROOT}formations/${formation.slug}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body,
      });

      if (!res.ok) throw new Error(await getResponseError(res));

      const data = await res.json();
      this.setState({ formation: data, saving: false, saveSuccess: true });
      setTimeout(() => this.setState({ saveSuccess: false }), 3000);
    } catch (err) {
      this.setState({ saving: false, saveError: getNetworkError(err) });
    }
  };

  updateFormationField = (field, value) => {
    this.setState((prev) => ({
      formation: { ...prev.formation, [field]: value },
    }));
  };

  handlePortraitFile = (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      this.setState({ portraitError: "La imagen supera el límite de 2 MB. Optimízala antes de subirla." });
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      this.updateFormationField("portrait", e.target.result);
      this.setState({ portraitError: "" });
    };
    reader.readAsDataURL(file);
  };

  toggleBlock = (blockId) => {
    this.setState((prev) => ({
      expandedBlocks: {
        ...prev.expandedBlocks,
        [blockId]: !prev.expandedBlocks[blockId],
      },
    }));
  };

  toggleEditBlock = (blockId) => {
    this.setState((prev) => ({
      editingBlocks: {
        ...prev.editingBlocks,
        [blockId]: !prev.editingBlocks[blockId],
      },
    }));
  };

  moveBlock = (blockId, direction) => {
    this.setState((prev) => {
      const blocks = [...prev.formation.blocks];
      const idx = blocks.findIndex((b) => b._id === blockId);
      const target = idx + direction;
      if (target < 0 || target >= blocks.length) return null;
      [blocks[idx], blocks[target]] = [blocks[target], blocks[idx]];
      return { formation: { ...prev.formation, blocks } };
    });
  };

  moveSection = (blockId, sectionId, direction) => {
    this.setState((prev) => {
      const blocks = prev.formation.blocks.map((b) => {
        if (b._id !== blockId) return b;
        const sections = [...b.sections];
        const idx = sections.findIndex((s) => s._id === sectionId);
        const target = idx + direction;
        if (target < 0 || target >= sections.length) return b;
        [sections[idx], sections[target]] = [sections[target], sections[idx]];
        return { ...b, sections };
      });
      return { formation: { ...prev.formation, blocks } };
    });
  };

  updateSectionContent = (blockId, sectionId, contentKey, value) => {
    this.setState((prev) => ({
      formation: {
        ...prev.formation,
        blocks: prev.formation.blocks.map((b) =>
          b._id !== blockId
            ? b
            : {
                ...b,
                sections: b.sections.map((s) =>
                  s._id !== sectionId ? s : { ...s, [contentKey]: value },
                ),
              },
        ),
      },
    }));
  };

  addBlock = async () => {
    const { formation, newBlockForm } = this.state;
    if (!newBlockForm.name.trim() || !newBlockForm.mode) return;
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(
        `${API_ROOT}formations/${formation.slug}/blocks`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            name: newBlockForm.name.trim(),
            mode: newBlockForm.mode,
            pass:
              newBlockForm.mode === "test" && newBlockForm.pass !== ""
                ? Number(newBlockForm.pass)
                : null,
          }),
        },
      );
      if (!res.ok) throw new Error(await getResponseError(res));
      const data = await res.json();
      const lastBlock = data.blocks[data.blocks.length - 1];
      this.setState({
        formation: data,
        newBlockForm: { name: "", mode: "", pass: "" },
        selectedBlockId: lastBlock?._id || null,
      });
    } catch (err) {
      alert(getNetworkError(err));
    }
  };

  deleteBlock = async (blockId) => {
    const { formation } = this.state;
    const accept = window.confirm(
      "¿Estás seguro de que quieres eliminar este bloque? Esta acción no se puede deshacer.",
    );
    if (!accept) return;
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(
        `${API_ROOT}formations/${formation.slug}/blocks/${blockId}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (!res.ok) throw new Error(await getResponseError(res));
      const data = await res.json();
      this.setState((prev) => ({
        formation: data,
        selectedBlockId:
          prev.selectedBlockId === blockId
            ? data.blocks[0]?._id || null
            : prev.selectedBlockId,
      }));
    } catch (err) {
      alert(getNetworkError(err));
    }
  };

  addSection = async (blockId) => {
    const { formation, newSectionForms } = this.state;
    const form = newSectionForms[blockId] || { name: "", type: "text" };
    if (!form.name.trim()) return;
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(
        `${API_ROOT}formations/${formation.slug}/blocks/${blockId}/sections`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ name: form.name.trim(), type: form.type }),
        },
      );
      if (!res.ok) throw new Error(await getResponseError(res));
      const data = await res.json();
      const updatedBlock = data.blocks.find((b) => b._id === blockId);
      const defaultType = updatedBlock?.mode === "test" ? "quiz" : "text";
      this.setState((prev) => ({
        formation: data,
        newSectionForms: {
          ...prev.newSectionForms,
          [blockId]: { name: "", type: defaultType },
        },
      }));
    } catch (err) {
      alert(getNetworkError(err));
    }
  };

  deleteSection = async (blockId, sectionId) => {
    const { formation } = this.state;
    const accept = window.confirm(
      "¿Estás seguro de que quieres eliminar esta sección? Esta acción no se puede deshacer.",
    );
    if (!accept) return;

    try {
      const token = localStorage.getItem("token");
      const res = await fetch(
        `${API_ROOT}formations/${formation.slug}/blocks/${blockId}/sections/${sectionId}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (!res.ok) throw new Error(await getResponseError(res));
      const data = await res.json();
      this.setState({ formation: data });
    } catch (err) {
      alert(getNetworkError(err));
    }
  };

  renderContentEditor() {
    const { formation, selectedBlockId, newBlockForm, portraitError } =
      this.state;
    if (!formation) return null;

    const block =
      formation.blocks.find((b) => b._id === selectedBlockId) || null;
    const blockIdx = formation.blocks.findIndex(
      (b) => b._id === selectedBlockId,
    );
    const isEditing = block ? !!this.state.editingBlocks[block._id] : false;
    const showNewBlock = selectedBlockId === "__new__";

    return (
      <div className="training-card  mb-3 p-3">
        {/* Formación info */}
        <div className="form-group mb-2">
          <label className="form-label">Nombre</label>
          <input
            type="text"
            className="form-control"
            value={formation.name}
            onChange={(e) => this.updateFormationField("name", e.target.value)}
          />
        </div>
        <div className="form-group mb-2">
          <label className="form-label">Descripción</label>
          <textarea
            className="form-control"
            rows="2"
            value={formation.description || ""}
            onChange={(e) =>
              this.updateFormationField("description", e.target.value)
            }
          />
        </div>
        <div className="form-group mb-2">
          <label className="form-label">Portada</label>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              cursor: "pointer",
              width: "100%",
            }}
          >
            <span
              style={{
                padding: "6px 14px",
                borderRadius: 6,
                border: "1px solid #ced4da",
                background: "#f8f9fa",
                color: "#495057",
                fontSize: 14,
                fontWeight: 500,
                whiteSpace: "nowrap",
                userSelect: "none",
              }}
            >
              Elegir imagen
            </span>
            <span
              style={{
                flex: 1,
                fontSize: 14,
                color: "#6c757d",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {this.state.portraitFileName || "Sin archivo seleccionado"}
            </span>
            <input
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={(e) => {
                const file = e.target.files[0];
                this.setState({ portraitFileName: file ? file.name : null });
                this.handlePortraitFile(file);
              }}
            />
          </label>
          {portraitError && (
            <div className="text-danger small mt-1">{portraitError}</div>
          )}
          {formation.portrait && (
            <div className="mt-2 d-flex align-items-center gap-2">
              <img
                src={formation.portrait}
                alt="preview"
                style={{
                  maxHeight: 80,
                  maxWidth: 120,
                  objectFit: "contain",
                  borderRadius: 4,
                  border: "1px solid #e2e8f0",
                }}
              />
              <button
                type="button"
                className="btn btn-sm btn-outline-danger"
                onClick={() => this.updateFormationField("portrait", null)}
              >
                <i className="fas fa-times" /> Quitar
              </button>
            </div>
          )}
        </div>
        <div className="d-flex align-items-center gap-3 mb-3">
          <input
            type="checkbox"
            className="training-checkbox"
            id="edit-mandatory"
            checked={formation.mandatory || false}
            onChange={(e) =>
              this.updateFormationField("mandatory", e.target.checked)
            }
          />
          <label className="training-checkbox-label mr-2" htmlFor="edit-mandatory">
            Mostrar/Ocultar
          </label>
          <input
            type="checkbox"
            className="training-checkbox"
            id="edit-always-accessible"
            checked={formation.alwaysAccessible || false}
            onChange={(e) =>
              this.updateFormationField("alwaysAccessible", e.target.checked)
            }
          />
          <label
            className="training-checkbox-label"
            htmlFor="edit-always-accessible"
          >
            Quitar orden
          </label>
        </div>

        {/* Selector de bloques */}
        <div className="tef-block-tabs">
          {formation.blocks.map((b, i) => (
            <button
              key={b._id}
              type="button"
              className={`tef-block-tab${selectedBlockId === b._id ? " active" : ""}`}
              onClick={() => this.setState({ selectedBlockId: b._id })}
            >
              {i + 1}
            </button>
          ))}
          <button
            type="button"
            className={`tef-block-tab tef-block-tab-add${showNewBlock ? " active" : ""}`}
            onClick={() => this.setState({ selectedBlockId: "__new__" })}
          >
            +
          </button>
        </div>

        {/* Panel bloque seleccionado */}
        {block && (
          <div className="tef-block-panel mt-3">
            <div className="tef-block-header-left">
              <div className="tef-order-btns tef-order-btns-row">
                <button
                  className="tef-order-btn"
                  disabled={blockIdx === 0}
                  onClick={() => this.moveBlock(block._id, -1)}
                >
                  ◀
                </button>
                <button
                  className="tef-order-btn"
                  disabled={blockIdx === formation.blocks.length - 1}
                  onClick={() => this.moveBlock(block._id, 1)}
                >
                  ▶
                </button>
              </div>
              <h6 style={{ margin: 0 }}>{block.name}</h6>
              <label className="blocks-list-badge badge-mode">
                {block.mode}
              </label>
              {block.mode === "test" && block.pass != null && (
                <label className="blocks-list-badge badge-pass">
                  {block.pass}%
                </label>
              )}
              <button
                className="tef-edit-btn"
                onClick={() => this.toggleEditBlock(block._id)}
              >
                <i className="fas fa-pen" />
              </button>
              <button
                className="btn btn-sm btn-outline-danger tef-edit-btn"
                onClick={() => this.deleteBlock(block._id)}
              >
                <i className="fas fa-trash" />
              </button>
            </div>
            {isEditing && (
              <div className="tef-block-edit-panel">
                <input
                  type="text"
                  className="tef-block-edit-input"
                  value={block.name}
                  onChange={(e) => {
                    const v = e.target.value;
                    this.setState((prev) => ({
                      formation: {
                        ...prev.formation,
                        blocks: prev.formation.blocks.map((b) =>
                          b._id === block._id ? { ...b, name: v } : b,
                        ),
                      },
                    }));
                  }}
                />
                <select
                  className="tef-block-edit-input"
                  value={block.mode}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === "test") {
                      const nonQuiz = block.sections.filter(
                        (s) => s.type !== "quiz",
                      );
                      if (nonQuiz.length > 0) {
                        const ok = window.confirm(
                          `Al cambiar a modo test se eliminarán ${nonQuiz.length} sección(es) que no son de tipo quiz. ¿Continuar?`,
                        );
                        if (!ok) return;
                      }
                      this.setState((prev) => ({
                        formation: {
                          ...prev.formation,
                          blocks: prev.formation.blocks.map((b) =>
                            b._id === block._id
                              ? {
                                  ...b,
                                  mode: v,
                                  sections: b.sections.filter(
                                    (s) => s.type === "quiz",
                                  ),
                                }
                              : b,
                          ),
                        },
                      }));
                    } else {
                      this.setState((prev) => ({
                        formation: {
                          ...prev.formation,
                          blocks: prev.formation.blocks.map((b) =>
                            b._id === block._id
                              ? { ...b, mode: v, pass: null }
                              : b,
                          ),
                        },
                      }));
                    }
                  }}
                >
                  <option value="teoria">teoria</option>
                  <option value="test">test</option>
                </select>
                {block.mode === "test" && (
                  <input
                    type="number"
                    min="0"
                    max="100"
                    className="tef-block-edit-input"
                    value={block.pass ?? ""}
                    onChange={(e) => {
                      const v = e.target.value;
                      this.setState((prev) => ({
                        formation: {
                          ...prev.formation,
                          blocks: prev.formation.blocks.map((b) =>
                            b._id === block._id ? { ...b, pass: v } : b,
                          ),
                        },
                      }));
                    }}
                  />
                )}
              </div>
            )}

            {/* Secciones */}
            <div className="mt-3">
              {block.sections.map((section, secIdx) => {
                const totalSections = block.sections.length;
                return (
                  <div
                    key={section._id}
                    className="training-card training-accent-sections mb-3 p-3"
                  >
                    <div className="tef-section-header">
                      <div className="tef-order-btns">
                        <button
                          className="tef-order-btn"
                          disabled={secIdx === 0}
                          onClick={() =>
                            this.moveSection(block._id, section._id, -1)
                          }
                        >
                          ▲
                        </button>
                        <button
                          className="tef-order-btn"
                          disabled={secIdx === totalSections - 1}
                          onClick={() =>
                            this.moveSection(block._id, section._id, 1)
                          }
                        >
                          ▼
                        </button>
                      </div>
                      <span
                        className={`blocks-list-badge badge-${section.type}`}
                      >
                        {section.type}:{secIdx}
                      </span>
                      <h6 className="tef-section-title">{section.name}</h6>
                      <button
                        className="btn btn-sm btn-outline-danger tef-section-delete-btn"
                        onClick={() =>
                          this.deleteSection(block._id, section._id)
                        }
                      >
                        <i className="fas fa-trash" />
                      </button>
                    </div>
                    {section.type === "text" && (
                      <TrainingTextEditor
                        value={section.textContent?.body}
                        image={section.textContent?.image}
                        onChange={(val) =>
                          this.updateSectionContent(
                            block._id,
                            section._id,
                            "textContent",
                            val,
                          )
                        }
                      />
                    )}
                    {section.type === "video" && (
                      <TrainingVideoEditor
                        videoContent={section.videoContent}
                        onChange={(val) =>
                          this.updateSectionContent(
                            block._id,
                            section._id,
                            "videoContent",
                            val,
                          )
                        }
                      />
                    )}
                    {section.type === "slider" && (
                      <TrainingSliderEditor
                        cards={section.sliderContent?.cards || []}
                        onChange={(cards) =>
                          this.updateSectionContent(
                            block._id,
                            section._id,
                            "sliderContent",
                            { cards },
                          )
                        }
                      />
                    )}
                    {section.type === "accordion" && (
                      <TrainingAccordionEditor
                        items={section.accordionContent?.items || []}
                        onChange={(items) =>
                          this.updateSectionContent(
                            block._id,
                            section._id,
                            "accordionContent",
                            { items },
                          )
                        }
                      />
                    )}
                    {section.type === "quiz" && (
                      <TrainingQuizEditor
                        quizContent={section.quizContent}
                        onChange={(val) =>
                          this.updateSectionContent(
                            block._id,
                            section._id,
                            "quizContent",
                            val,
                          )
                        }
                      />
                    )}
                  </div>
                );
              })}

              {/* Nueva sección */}
              {!(block.mode === "test" && block.sections.length > 0) && <div className="training-card training-accent-sections mb-3 p-3 tef-add-section-panel">
                <h2>Nueva Sección</h2>
                <input
                  type="text"
                  className="form-control form-control-sm tef-add-section-name"
                  placeholder="Nombre de la sección"
                  value={
                    (this.state.newSectionForms[block._id] || {}).name || ""
                  }
                  onChange={(e) =>
                    this.setState((prev) => ({
                      newSectionForms: {
                        ...prev.newSectionForms,
                        [block._id]: {
                          ...(prev.newSectionForms[block._id] || {
                            type: block.mode === "test" ? "quiz" : "text",
                          }),
                          name: e.target.value,
                        },
                      },
                    }))
                  }
                />
                <select
                  className="form-control form-control-sm tef-add-section-type"
                  value={
                    (this.state.newSectionForms[block._id] || {}).type ||
                    (block.mode === "test" ? "quiz" : "text")
                  }
                  onChange={(e) =>
                    this.setState((prev) => ({
                      newSectionForms: {
                        ...prev.newSectionForms,
                        [block._id]: {
                          ...(prev.newSectionForms[block._id] || { name: "" }),
                          type: e.target.value,
                        },
                      },
                    }))
                  }
                >
                  {block.mode === "test" ? (
                    <option value="quiz">quiz</option>
                  ) : (
                    <>
                      <option value="text">text</option>
                      <option value="video">video</option>
                      <option value="slider">slider</option>
                      <option value="accordion">accordion</option>
                      <option value="quiz">quiz</option>
                    </>
                  )}
                </select>
                <button
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => this.addSection(block._id)}
                >
                  <i className="fas fa-plus" /> Añadir
                </button>
              </div>}
            </div>
          </div>
        )}

        {/* Panel nuevo bloque */}
        {showNewBlock && (
          <div className="tef-block-panel mt-3 tef-add-section-panel">
            <h2>Nuevo Bloque</h2>
            <input
              type="text"
              className="form-control form-control-sm tef-add-section-name"
              placeholder="Nombre del bloque"
              value={newBlockForm.name}
              onChange={(e) =>
                this.setState((prev) => ({
                  newBlockForm: { ...prev.newBlockForm, name: e.target.value },
                }))
              }
            />
            <select
              className="form-control form-control-sm tef-add-section-type"
              value={newBlockForm.mode}
              onChange={(e) =>
                this.setState((prev) => ({
                  newBlockForm: {
                    ...prev.newBlockForm,
                    mode: e.target.value,
                    pass: "",
                  },
                }))
              }
            >
              <option value="">Selecciona un modo...</option>
              <option value="teoria">teoria</option>
              <option value="test">test</option>
            </select>
            {newBlockForm.mode === "test" && (
              <input
                type="number"
                min="0"
                max="100"
                className="form-control form-control-sm tef-add-section-name"
                placeholder="Nota mín. (%)"
                value={newBlockForm.pass}
                onChange={(e) =>
                  this.setState((prev) => ({
                    newBlockForm: {
                      ...prev.newBlockForm,
                      pass: e.target.value,
                    },
                  }))
                }
              />
            )}
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={this.addBlock}
            >
              <i className="fas fa-plus" /> Añadir bloque
            </button>
          </div>
        )}
      </div>
    );
  }

  render() {
    const { formation, loading, error, saving, saveError, saveSuccess } =
      this.state;
    if (loading) return <div>Cargando...</div>;
    if (error) return <div>Error: {error}</div>;

    return (
      <div>
        <TrainingPageHeader
          icon={<NotebookTabs size={30} />}
          title="Training Editor"
        />
        {this.renderContentEditor()}
        <div className="submit-area mt-3">
          {saveSuccess && (
            <div className="submit-feedback success">
              Cambios guardados correctamente.
            </div>
          )}
          {saveError && (
            <div className="submit-feedback error">{saveError}</div>
          )}
          <button
            className="btn btn-primary"
            onClick={this.handleUpdate}
            disabled={saving}
          >
            {saving ? "Guardando..." : "Guardar Cambios"}
          </button>
        </div>
      </div>
    );
  }
}

export default TrainingEditForm;
